import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { TenantRole, type Member } from '@etiquetachef/types';
import { toast } from '@/lib/notifications';
import { setMemberPasswordCallable, setStaffPinCallable } from '@/lib/callables';

type Props = {
  tenantId: string;
  /** Membro-alvo; `null` fecha o diálogo. STAFF → PIN; demais → senha. */
  member: Member | null;
  onClose: () => void;
};

const PIN_RE = /^\d{6}$/;

/**
 * Troca a credencial de um membro pelo painel super-admin. STAFF usa PIN de 6
 * dígitos (`setStaffPin`); OWNER/MANAGER usam senha real ≥6 (`setMemberPassword`)
 * — espelha o `ChangePasswordDialog` de `apps/app`.
 *
 * Origem: sem issue — commit f948c64e4, 2026-06-10 18:39 +0200.
 *
 * Contrato: `setStaffPin({ tenantId, targetUid, newPin })` ou
 * `setMemberPassword({ tenantId, targetUid, newPassword })` (6–72 caracteres;
 * teto do `StaffPasswordSchema`, "limite bcrypt", em
 * `packages/types/src/schemas.ts`). Qualquer erro vira a mensagem genérica
 * `credentialFailed`. `member = null` não renderiza nada.
 *
 * Usado por: `features/members/MembersTab.tsx` (aba Usuários do tenant) e
 * `routes/UsersPage.tsx` (tela global de Usuários, depois de escolher a
 * empresa no `TenantPickerDialog`).
 *
 * Arquitetura: os callables (`apps/functions/src/setStaffPin.ts`,
 * `setMemberPassword.ts`) revalidam o papel do alvo e a permissão de quem
 * chama; o cliente só escolhe qual chamar pelo `member.role`.
 */
export function MemberCredentialDialog({ tenantId, member, onClose }: Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue('');
    setError(null);
  }, [member]);

  if (!member) return null;

  const isStaff = member.role === TenantRole.STAFF;
  const canSubmit = isStaff ? PIN_RE.test(value) : value.length >= 6 && value.length <= 72;

  async function submit(): Promise<void> {
    if (!member) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isStaff) {
        await setStaffPinCallable({ tenantId, targetUid: member.uid, newPin: value });
        toast.success(t('adminMembers.toast.pinChanged'));
      } else {
        await setMemberPasswordCallable({ tenantId, targetUid: member.uid, newPassword: value });
        toast.success(t('adminMembers.toast.passwordChanged'));
      }
      onClose();
    } catch (err) {
      setError(t('adminMembers.errors.credentialFailed'));
      console.error('memberCredential submit failed', err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible
      onDismiss={(): void => {
        if (!submitting) onClose();
      }}
      header={
        isStaff
          ? t('adminMembers.credentialDialog.titlePin', { name: member.displayName })
          : t('adminMembers.credentialDialog.titlePassword', { name: member.displayName })
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={submitting}>
              {t('adminMembers.addDialog.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void submit();
              }}
              loading={submitting}
              disabled={!canSubmit}
            >
              {t('adminMembers.credentialDialog.submit')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField
          label={isStaff ? t('adminMembers.credentialDialog.pin') : t('adminMembers.credentialDialog.password')}
          description={
            isStaff
              ? t('adminMembers.credentialDialog.pinHint')
              : t('adminMembers.credentialDialog.passwordHint')
          }
        >
          <Input
            value={value}
            onChange={({ detail }): void => {
              setValue(isStaff ? detail.value.replace(/\D/g, '').slice(0, 6) : detail.value);
            }}
            disabled={submitting}
            type="password"
            inputMode={isStaff ? 'numeric' : 'text'}
          />
        </FormField>
        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
